/**
 * Storefront OAuth helpers (Google / Facebook).
 * Start: GET /api/auth/v1/{provider}/init?redirectUri=… (via axios baseURL `/api/`).
 * Finish: API handles the provider callback, then redirects the browser to the SPA with
 * `/oauth/{provider}/complete?token=…&refreshToken=…` (`refreshToken` optional for the SPA).
 */

export const OAUTH_PROVIDERS = ["google", "facebook"] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

export function isOAuthProvider(value: string): value is OAuthProvider {
  return (OAUTH_PROVIDERS as readonly string[]).includes(value);
}

/** Frontend route that completes OAuth for a provider (not a backend API path). */
export function oauthSpaCallbackPath(provider: OAuthProvider): string {
  return `/oauth/${provider}/complete`;
}

/** Backend init endpoint (relative to axios `/api/` base). */
export function oauthInitApiPath(provider: OAuthProvider): string {
  return `auth/v1/${provider}/init`;
}

/** Backend callback endpoint (relative to axios `/api/` base). */
export function oauthCallbackApiPath(provider: OAuthProvider): string {
  return `auth/v1/${provider}/callback`;
}

/** Absolute same-origin SPA callback URL passed as client redirectUri after tokens are issued. */
export function buildOAuthSpaRedirectUri(
  provider: OAuthProvider,
  origin: string = window.location.origin
): string {
  const base = origin.replace(/\/+$/, "");
  return `${base}${oauthSpaCallbackPath(provider)}`;
}

/**
 * Extract authorization URL from flat or envelope API responses.
 * Supports:
 * - `{ authUrl: "..." }`
 * - `{ success: true, data: { authUrl: "..." } }`
 */
export function extractOAuthAuthUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;

  if (typeof root.authUrl === "string" && root.authUrl.length > 0) {
    return root.authUrl;
  }

  const data = root.data;
  if (data && typeof data === "object") {
    const nested = (data as Record<string, unknown>).authUrl;
    if (typeof nested === "string" && nested.length > 0) {
      return nested;
    }
  }

  return null;
}

export function oauthProviderDisplayName(provider: OAuthProvider): string {
  switch (provider) {
    case "facebook":
      return "Facebook";
    case "google":
    default:
      return "Google";
  }
}

/** @deprecated Prefer oauthSpaCallbackPath("google") */
export const GOOGLE_OAUTH_SPA_CALLBACK_PATH = oauthSpaCallbackPath("google");
/** @deprecated Prefer oauthInitApiPath("google") */
export const GOOGLE_INIT_API_PATH = oauthInitApiPath("google");
/** @deprecated Prefer oauthCallbackApiPath("google") */
export const GOOGLE_CALLBACK_API_PATH = oauthCallbackApiPath("google");

/** @deprecated Prefer buildOAuthSpaRedirectUri("google", origin) */
export function buildGoogleOAuthSpaRedirectUri(
  origin: string = window.location.origin
): string {
  return buildOAuthSpaRedirectUri("google", origin);
}

/** @deprecated Prefer extractOAuthAuthUrl */
export const extractGoogleAuthUrl = extractOAuthAuthUrl;
